import { Divider, Stack, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React from 'react';

import WsColors from '../../WsColors';

export enum TabTextVariant {
  TITLE = '16px',
  SUB_TITLE = '14px',
  BODY1 = '12px',
}

export enum TabTextColorVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

const mapColor = new Map();
mapColor.set('primary', WsColors.TertiaryShades600);
mapColor.set('secondary', WsColors.GenericRichBlack);

const ScrollTab = styled(Tab)(() => ({
  textTransform: 'none',
}));

export const commonTabStyling = {
  height: '24px',
  width: '28px',
  lineHeight: 'none',
  fontSize: '16px',
  marginLeft: '12px',
};

export const InActiveTab = styled(Avatar)(() => ({
  ...commonTabStyling,
  backgroundColor: WsColors.TertiaryShades200,
  color: WsColors.TertiaryShades600,
}));

export const ActiveTab = styled(Avatar)(() => ({
  ...commonTabStyling,
  backgroundColor: WsColors.TheBlues500,
  color: WsColors.TheBlues900,
}));

interface IScrollList {
  tabName: string;
  tabCount?: number;
  icon?: string | React.ReactElement<any, string | React.JSXElementConstructor<any>>;
}
interface IScrollableTabs {
  scrollList: IScrollList[];
  getTabIndex(args: number): void;
  variant?: TabTextVariant;
  textColor?: TabTextColorVariant;
  defaultValue?: number;
  className?: string;
  divider?: boolean;
  visibleScrollbar?: boolean;
  scrollButtons?: 'auto' | true | false;
}

const ScrollableTabs: React.FC<IScrollableTabs> = ({
  scrollList,
  getTabIndex,
  variant = TabTextVariant.TITLE,
  textColor = TabTextColorVariant.PRIMARY,
  defaultValue,
  className,
  divider = true,
  visibleScrollbar = false,
  scrollButtons = 'auto',
}) => {
  const [value, setValue] = React.useState(0);
  const [scrollArray, setScrollArray] = React.useState<IScrollList[]>([]);

  React.useEffect(() => {
    setScrollArray(scrollList);
  }, [scrollList]);
  React.useEffect(() => {
    setValue(defaultValue ?? 0);
  }, [defaultValue]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    getTabIndex(newValue);
    setValue(newValue);
  };

  const getTab = (id: number, value: number, total: string | number) => {
    if (id === value) {
      return (
        <ActiveTab className={'tabAvatar'} variant="rounded">
          {total}
        </ActiveTab>
      );
    }
    return (
      <InActiveTab className={'tabAvatar'} variant="rounded">
        {total}
      </InActiveTab>
    );
  };

  const addTabElement = (total: number | string, tabLabel: string, id: number) => (
    <Stack sx={id !== value ? { color: mapColor.get(textColor) } : {}} direction="row" spacing={1}>
      {tabLabel}
      {total !== '' ? getTab(id, value, total) : null}
    </Stack>
  );

  return (
    <>
      <Tabs
        value={value}
        onChange={handleChange}
        className={className}
        variant="scrollable"
        scrollButtons={scrollButtons}
        visibleScrollbar={visibleScrollbar}
        allowScrollButtonsMobile
        aria-label="scrollable force tabs example"
      >
        {scrollArray.map((tab: IScrollList, index: number) => {
          return (
            <ScrollTab
              sx={{
                fontSize: variant,
              }}
              icon={tab.icon}
              iconPosition="start"
              key={index}
              label={addTabElement(tab.tabCount ? tab.tabCount : '', tab.tabName, index)}
            />
          );
        })}
      </Tabs>
      {divider && <Divider sx={{ marginLeft: '19px', marginRight: '19px' }} />}
    </>
  );
};

export default ScrollableTabs;
